// Transformation utilities for orderless return data mapping

import type { CreateOrderlessReturnVariables } from '../graphql/mutation/createOrderlessReturn';
import type { OrderlessCustomerData } from '../components/tasks/OrderlessTask/OrderlessTaskSchema';
import { OrderlessGraphQLInputSchema } from '../components/tasks/OrderlessTask/OrderlessTaskSchema';

// Extract the GraphQL input type for type safety
export type OrderlessGraphQLInput = CreateOrderlessReturnVariables['input'];

/**
 * Transform react-hook-form data to a GraphQL‑aligned input structure.
 * Maps the nested form `address` object to top‑level GraphQL address fields.
 */
export const transformFormDataToGraphQLInput = (
  formData: OrderlessCustomerData['orderlessCustomer'],
): Omit<OrderlessGraphQLInput, 'items'> => {
  const graphQLInput = {
    email: formData.email,
    firstName: formData.firstName,
    lastName: formData.lastName,
    street1: formData.address.address1,
    street2: formData.address.address2 || undefined,
    city: formData.address.city,
    regionCode: formData.address.province || undefined,
    postalCode: formData.address.postalCode || undefined,
    countryCode: formData.address.countryCode,
    language: formData.language,
    currencyCode: formData.currencyCode,
    phone: formData.phone || undefined,
    phoneExtension: formData.phoneExtension || undefined,
  } satisfies Partial<OrderlessGraphQLInput>;

  // Validate the shape against the zod schema to ensure alignment with backend
  const parsed = OrderlessGraphQLInputSchema.safeParse(graphQLInput);
  if (!parsed.success) {
    // Throwing here ensures callers surface a clear error in development/test
    throw new Error('Invalid orderless input mapping');
  }
  return parsed.data as Omit<OrderlessGraphQLInput, 'items'>;
};

/**
 * Create complete GraphQL input with customer data and items
 * Centralizes the final input construction with default items
 */
export const createOrderlessReturnInput = (
  customerData: Omit<OrderlessGraphQLInput, 'items'>,
  items: OrderlessGraphQLInput['items'] = [
    {
      item: {
        itemId: 'placeholder-item-id',
        sku: 'placeholder-sku',
        name: 'Placeholder Item',
        description: 'Test item for orderless return',
      },
      quantity: 1,
      price: 0.0,
    },
  ],
): OrderlessGraphQLInput => {
  return {
    ...customerData,
    items,
  };
};

/**
 * Helper function that combines both transformations for convenience
 * Transforms form data directly to complete GraphQL input
 */
export const transformToGraphQLInput = (
  formData: OrderlessCustomerData['orderlessCustomer'],
  items?: OrderlessGraphQLInput['items'],
): OrderlessGraphQLInput => {
  const customerData = transformFormDataToGraphQLInput(formData);
  return createOrderlessReturnInput(customerData, items);
};
